import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { DiscountMode } from '~/lib/schemas/actions';

interface Props {
	value: DiscountMode;
	onChange: (next: DiscountMode) => void;
	disabled?: boolean;
	className?: string;
}

const OPTIONS: { mode: DiscountMode; symbol: string; labelKey: string }[] = [
	{ mode: 'percentage', symbol: '%', labelKey: 'promotion.action.discountMode.percentage' },
	{ mode: 'amount', symbol: '€', labelKey: 'promotion.action.discountMode.amount' }
];

/**
 * Compact % / € segmented toggle for a row's `DiscountMode` (Percentage=0 / Amount=1 per unit).
 * Defaults to percentage, matching the server default for payloads without the field.
 */
export function DiscountModeInput({ value, onChange, disabled, className }: Props) {
	const { t } = useTranslation();
	return (
		<div role="radiogroup" className={cn('inline-flex h-8 overflow-hidden rounded-md border border-border', className)}>
			{OPTIONS.map(o => {
				const active = value === o.mode;
				return (
					<button
						key={o.mode}
						type="button"
						role="radio"
						aria-checked={active}
						aria-label={t(o.labelKey)}
						title={t(o.labelKey)}
						disabled={disabled}
						onClick={() => onChange(o.mode)}
						className={cn(
							'flex-1 px-2.5 text-sm font-semibold transition-colors disabled:opacity-50',
							active ? 'bg-brand text-on-brand' : 'bg-surface text-foreground-muted hover:bg-muted hover:text-foreground'
						)}>
						{o.symbol}
					</button>
				);
			})}
		</div>
	);
}
